import { useState, useEffect, useRef } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";

export const COOKIE_EXPIRATION_DAYS = 14;
export const BANNER_DISPLAY_DELAY = 3000; // Reduced to 3 seconds to show before popups

export interface CookieConsentStatus {
  isVisible: boolean;
  cookiesAccepted: string | null;
  acceptedDate: string | null;
}

export const useCookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { initializeAnalytics, trackEvent } = useAnalytics();
  const bannerShownRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Set default consent with analytics allowed for basic tracking
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'default', {
        'analytics_storage': 'granted',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'functionality_storage': 'granted',
        'personalization_storage': 'denied',
        'security_storage': 'granted'
      });
      console.log("Default consent set with basic analytics granted");
    }
    
    // Initialize basic analytics immediately
    if (!localStorage.getItem("cookiesAccepted")) {
      console.log("Initializing basic analytics for tracking");
      initializeAnalytics();
    }
    
    if (bannerShownRef.current) {
      return;
    }
    
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");
    const acceptedDate = localStorage.getItem("cookiesAcceptedDate");
    
    let shouldShowBanner = false;
    
    if (!cookiesAccepted) {
      shouldShowBanner = true;
    } else if (acceptedDate) {
      const expirationDate = new Date(acceptedDate);
      expirationDate.setDate(expirationDate.getDate() + COOKIE_EXPIRATION_DAYS);
      
      if (new Date() > expirationDate) {
        shouldShowBanner = true;
      }
    }
    
    if (shouldShowBanner) {
      bannerShownRef.current = true;
      
      // Wait for page to be fully loaded before showing banner
      const showBanner = () => {
        // Check if page is loaded and FCP has occurred
        if (document.readyState === 'complete') {
          timerRef.current = window.setTimeout(() => {
            setIsVisible(true);
            document.body.style.overflow = "hidden";
          }, BANNER_DISPLAY_DELAY);
        } else {
          // Wait for page load completion
          window.addEventListener('load', () => {
            timerRef.current = window.setTimeout(() => {
              setIsVisible(true);
              document.body.style.overflow = "hidden";
            }, BANNER_DISPLAY_DELAY);
          }, { once: true });
        }
      };
      
      showBanner();
      
      // Fallback timer - ensure analytics works even without interaction
      fallbackTimerRef.current = window.setTimeout(() => {
        if (!localStorage.getItem("cookiesAccepted")) {
          console.log("Fallback: Ensuring analytics is initialized");
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'page_view', {
              page_title: document.title,
              page_location: window.location.href,
              consent_status: 'basic_only'
            });
          }
        }
      }, 12000); // Increased fallback delay
      
      return () => {
        if (timerRef.current) {
          window.clearTimeout(timerRef.current);
        }
        if (fallbackTimerRef.current) {
          window.clearTimeout(fallbackTimerRef.current);
        }
      };
    } else {
      if (cookiesAccepted === 'all') {
        initializeAnalytics();
        
        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', {
            'analytics_storage': 'granted',
            'ad_storage': 'granted',
            'ad_user_data': 'granted',
            'ad_personalization': 'granted',
            'functionality_storage': 'granted',
            'personalization_storage': 'granted',
            'security_storage': 'granted'
          });
          console.log("Updated consent to full tracking");
        }
      }
    }
  }, [initializeAnalytics]);

  const acceptAllCookies = () => {
    localStorage.setItem("cookiesAccepted", "all");
    localStorage.setItem("cookiesAcceptedDate", new Date().toISOString());
    setIsVisible(false);
    document.body.style.overflow = "auto";
    
    // Clear timers
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
    
    initializeAnalytics();
    
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted',
        'security_storage': 'granted'
      });
      
      console.log("Full consent granted - all tracking enabled");
    }
    
    trackEvent('cookie_consent', { 
      consent_type: 'all',
      timestamp: new Date().toISOString()
    });
  };

  const acceptNecessaryCookies = () => {
    localStorage.setItem("cookiesAccepted", "necessary");
    localStorage.setItem("cookiesAcceptedDate", new Date().toISOString());
    setIsVisible(false);
    document.body.style.overflow = "auto";
    
    // Clear timers
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
    
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted', // Keep basic analytics
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'functionality_storage': 'granted',
        'personalization_storage': 'denied',
        'security_storage': 'granted'
      });
      
      console.log("Necessary consent only - basic analytics enabled");
    }
    
    trackEvent('cookie_consent', { 
      consent_type: 'necessary',
      timestamp: new Date().toISOString()
    });
  };

  return {
    isVisible,
    setIsVisible,
    acceptAllCookies,
    acceptNecessaryCookies,
    status: (): CookieConsentStatus => ({
      isVisible,
      cookiesAccepted: localStorage.getItem('cookiesAccepted'),
      acceptedDate: localStorage.getItem('cookiesAcceptedDate')
    })
  };
};
